/**
 * STEWARD Chat Widget — Theme Palettes
 *
 * Defines the theming data structure and built-in color palettes for the
 * Lit-based chat widget. Each palette supplies CSS custom-property values
 * for light and dark modes, plus SVG gradient stops used by the animated
 * star icon and an RGB triplet for halo/focus shadows.
 *
 * To add a new palette, append an entry to `THEMES` following the
 * `ThemePalette` shape — the widget will pick it up automatically.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A complete color palette for both light and dark appearances. */
export interface ThemePalette {
  /** Human-readable palette name. */
  name: string;

  /** CSS custom-property values for light mode (keys omit the `--` prefix). */
  light: Record<string, string>;

  /** CSS custom-property values for dark mode (keys omit the `--` prefix). */
  dark: Record<string, string>;

  /** Color stops fed to the SVG gradient on the star icon. */
  svgGradient: {
    /** Outer stops of the left gradient. */
    leftEdge: string;
    /** Center stop of the left gradient. */
    leftMid: string;
    /** Outer stops of the right gradient. */
    rightEdge: string;
    /** Center stop of the right gradient. */
    rightMid: string;
  };

  /** RGB triplet for halo / focus-ring shadows, e.g. "212, 129, 47". */
  haloRgb: string;

  /**
   * The send glyph's colour on this palette's OWN signature fill, where
   * white does not read on it. Applied only while the signature is still
   * the palette's own and the glyph token is untouched (see _applyPalette),
   * so a page that brands the fill keeps the white glyph it always had.
   */
  signatureInk?: string;
}

// ---------------------------------------------------------------------------
// Palettes
// ---------------------------------------------------------------------------

/**
 * Registry of built-in theme palettes, keyed by slug.
 * The widget defaults to "soleum" when no palette (or an unknown name) is
 * requested.
 */
export const THEMES: Record<string, ThemePalette> = {
  // ── Soleum (default) ─────────────────────────────────────────────────
  // The original STEWARD palette — teal, copper, warm neutrals.
  soleum: {
    name: 'soleum',

    light: {
      'steward-primary': '#344D4F',
      'steward-accent': '#608386',
      'steward-signature': '#D4812F',
      'steward-bg': '#ffffff',
      'steward-bg-secondary': '#f2f5f5',
      'steward-text': '#0A0A0A',
      'steward-text-secondary': '#608386',
      'steward-border': '#d0d9da',
      'steward-user-bg': '#fdf0e0',
      'steward-user-text': '#0A0A0A',
      'steward-assistant-bg': '#F5F0EB',
      'steward-assistant-text': '#0A0A0A',
      'steward-error-bg': '#fef2f2',
      'steward-error-text': '#dc2626',
      'steward-code-bg': 'transparent',
      'steward-code-text': '#608386',
      'steward-signature-hover': '#b86d25',
    },

    dark: {
      'steward-primary': '#4a6d70',
      'steward-accent': '#8aabad',
      'steward-signature': '#e09840',
      'steward-bg': '#0A0A0A',
      'steward-bg-secondary': '#1a1f1f',
      'steward-text': '#f1f5f5',
      'steward-text-secondary': '#8aabad',
      'steward-border': '#2a3536',
      'steward-user-bg': '#3d5b5e',
      'steward-user-text': '#f1f5f5',
      'steward-assistant-bg': '#1a1f1f',
      'steward-assistant-text': '#f1f5f5',
      'steward-error-bg': '#450a0a',
      'steward-error-text': '#fca5a5',
      'steward-code-bg': '#1a1f1f',
      'steward-code-text': '#d0d9da',
      'steward-signature-hover': '#a06020',
    },

    svgGradient: {
      leftEdge: '#f0c88a',
      leftMid: '#D4812F',
      rightEdge: '#e6b570',
      rightMid: '#b86d25',
    },

    haloRgb: '212, 129, 47',

    // Ink on the copper (owner-ratified 2026-09-29): white measures 3.01:1
    // on #D4812F, ink 6.58:1; on the dark copper #e09840 white is 2.4:1.
    signatureInk: '#0A0A0A',
  },

  // ── Lux ──────────────────────────────────────────────────────────────
  // Minimal & modern, Swiss-inspired. High-contrast monochrome with an
  // electric-blue accent — clean and razor-sharp.
  lux: {
    name: 'lux',

    light: {
      'steward-primary': '#1a1a1a',
      'steward-accent': '#2563eb',
      'steward-signature': '#2563eb',
      'steward-bg': '#ffffff',
      'steward-bg-secondary': '#f5f5f5',
      'steward-text': '#0f0f0f',
      'steward-text-secondary': '#737373',
      'steward-border': '#e5e5e5',
      'steward-user-bg': '#eff6ff',
      'steward-user-text': '#0f0f0f',
      'steward-assistant-bg': '#f5f5f5',
      'steward-assistant-text': '#0f0f0f',
      'steward-error-bg': '#fef2f2',
      'steward-error-text': '#dc2626',
      'steward-code-bg': 'transparent',
      'steward-code-text': '#737373',
      'steward-signature-hover': '#1d4ed8',
    },

    dark: {
      'steward-primary': '#e0e0e0',
      'steward-accent': '#60a5fa',
      'steward-signature': '#3b82f6',
      'steward-bg': '#0f0f0f',
      'steward-bg-secondary': '#1a1a1a',
      'steward-text': '#f5f5f5',
      'steward-text-secondary': '#a3a3a3',
      'steward-border': '#262626',
      'steward-user-bg': '#1e3a5f',
      'steward-user-text': '#f5f5f5',
      'steward-assistant-bg': '#1a1a1a',
      'steward-assistant-text': '#f5f5f5',
      'steward-error-bg': '#450a0a',
      'steward-error-text': '#fca5a5',
      'steward-code-bg': '#18181b',
      'steward-code-text': '#e4e4e7',
      'steward-signature-hover': '#3b82f6',
    },

    svgGradient: {
      leftEdge: '#93c5fd',
      leftMid: '#2563eb',
      rightEdge: '#60a5fa',
      rightMid: '#1d4ed8',
    },

    haloRgb: '37, 99, 235',
  },

  // ── Zen ──────────────────────────────────────────────────────────────
  // Serene, wabi-sabi, nature-inspired. Muted earth tones with a
  // stone/moss palette — soft and calming.
  zen: {
    name: 'zen',

    light: {
      'steward-primary': '#5c6b5e',
      'steward-accent': '#8b7355',
      'steward-signature': '#8b7355',
      'steward-bg': '#faf8f5',
      'steward-bg-secondary': '#f0ece5',
      'steward-text': '#2d2d2a',
      'steward-text-secondary': '#7a7a72',
      'steward-border': '#ddd8cf',
      'steward-user-bg': '#f0ece5',
      'steward-user-text': '#2d2d2a',
      'steward-assistant-bg': '#f5f2ec',
      'steward-assistant-text': '#2d2d2a',
      'steward-error-bg': '#fef2f2',
      'steward-error-text': '#b45309',
      'steward-code-bg': 'transparent',
      'steward-code-text': '#7a7a72',
      'steward-signature-hover': '#6b5a42',
    },

    dark: {
      'steward-primary': '#8a9e8c',
      'steward-accent': '#c4a882',
      'steward-signature': '#b89c70',
      'steward-bg': '#1a1b18',
      'steward-bg-secondary': '#252620',
      'steward-text': '#e8e4dc',
      'steward-text-secondary': '#a3a098',
      'steward-border': '#3a3b35',
      'steward-user-bg': '#2e3329',
      'steward-user-text': '#e8e4dc',
      'steward-assistant-bg': '#222320',
      'steward-assistant-text': '#e8e4dc',
      'steward-error-bg': '#3d1c00',
      'steward-error-text': '#fbbf24',
      'steward-code-bg': '#252620',
      'steward-code-text': '#e8e4dc',
      'steward-signature-hover': '#c4a882',
    },

    svgGradient: {
      leftEdge: '#f0e6d2',
      leftMid: '#d4b896',
      rightEdge: '#e8d5b8',
      rightMid: '#c4a67a',
    },

    haloRgb: '139, 115, 85',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** All available palette slugs. */
export const PALETTE_NAMES = Object.keys(THEMES) as readonly string[];

/**
 * Returns the requested palette, falling back to "soleum" when the name is
 * unrecognised or omitted.
 */
export function getTheme(name: string): ThemePalette {
  return THEMES[name] ?? THEMES['soleum'];
}
